import type { PbjPlanActionProgress, PbjPlanProjection, PbjPlanValue } from "../../../utils/models";
import {
	type ActionFormat,
	formatPlanNumber,
	formatPlanValue,
	formatPlanValueInDirection,
	formatWholeNumber,
	movedBackwards,
	type PlanDirection,
	planValueOf,
	withUnit,
} from "./planValue.utils";

/**
 * An i18n key plus its interpolation values — every value already formatted for print, except `count`,
 * which i18next reads as a number to pick the plural form.
 */
export type PlanCopy = { key: string; values: Record<string, string | number> };

const inDirectionWithUnit = (value: PbjPlanValue, direction: PlanDirection, { basis, unit }: ActionFormat): string =>
	withUnit(formatPlanValueInDirection(value, basis, direction), unit);

/** A plan value as an unsigned magnitude with its unit — for copy that supplies its own direction. */
const magnitudeWithUnit = (value: PbjPlanValue, { basis, unit }: ActionFormat): string =>
	withUnit(formatPlanNumber(Math.abs(planValueOf(value, basis)), basis), unit);

/**
 * Why a lever reads behind, in its own numbers, with the way back.
 *
 * A cut that moved BACKWARDS gets its own wording: "cut −150 hrs" is a double negative nobody can read,
 * so it says what should have been cut and how far staffing rose instead, both as magnitudes.
 */
export const behindBannerCopy = (action: PbjPlanActionProgress, format: ActionFormat, perWeekSuffix: string): PlanCopy => {
	const isCut = action.direction === "cut";
	if (isCut && movedBackwards(action, format.basis)) {
		return {
			key: "pbj_model.tracker.behind_body_staffing_rose_cut",
			values: { required: magnitudeWithUnit(action.requiredToDate, format), rose: magnitudeWithUnit(action.delivered, format) },
		};
	}
	const required = inDirectionWithUnit(action.requiredToDate, action.direction, format);
	const delivered = inDirectionWithUnit(action.delivered, action.direction, format);
	if (action.updatedGoal === null) {
		return { key: isCut ? "pbj_model.tracker.behind_body_no_weeks_cut" : "pbj_model.tracker.behind_body_no_weeks", values: { required, delivered } };
	}
	return {
		key: isCut ? "pbj_model.tracker.behind_body_recover_cut" : "pbj_model.tracker.behind_body_recover",
		values: { required, delivered, catchup: `${inDirectionWithUnit(action.updatedGoal, action.direction, format)}${perWeekSuffix}` },
	};
};

/**
 * The projection in words. Points come from CROSSING a band edge, so a projection short of the goal
 * can still be worth every edge it clears; naming the points is how the reader learns which they have.
 *
 * A cut's projected points are what it LOSES. They are rounded before the sign is read, and a cut that
 * loses nothing — including one moving backwards, whose points come back positive — says so in words
 * rather than "losing 0 pts" or "losing −5 pts". `shortfall` is already a magnitude in the lever's
 * direction. The projected figure keeps its sign: a cut heading backwards really does end below zero.
 */
export const paceCopy = (action: PbjPlanActionProgress, projected: PbjPlanProjection, format: ActionFormat): PlanCopy => {
	const value = inDirectionWithUnit(projected.delivered, action.direction, format);
	const shortfall = withUnit(formatPlanValue(projected.shortfall, format.basis), format.unit);
	if (action.direction === "add") {
		const points = formatWholeNumber(projected.points);
		return projected.meetsGoal
			? { key: "pbj_model.tracker.pace_meets_goal", values: { value, points } }
			: { key: "pbj_model.tracker.pace_short", values: { value, shortfall, points } };
	}
	const pointsLost = Math.round(-projected.points);
	if (pointsLost > 0) {
		const points = `${pointsLost}`;
		return projected.meetsGoal
			? { key: "pbj_model.tracker.pace_meets_goal_cut", values: { value, points, count: pointsLost } }
			: { key: "pbj_model.tracker.pace_short_cut", values: { value, shortfall, points, count: pointsLost } };
	}
	return projected.meetsGoal
		? { key: "pbj_model.tracker.pace_meets_goal_cut_no_loss", values: { value } }
		: { key: "pbj_model.tracker.pace_short_cut_no_loss", values: { value, shortfall } };
};
